import { z } from 'zod';
import {
  dateRangeFields,
  isoDateSchema,
  isOrderedRange,
  ORDERED_RANGE_ISSUE,
  isoDateTimeSchema,
  longTextSchema,
  paginationSchema,
  positiveMoneySchema,
  shortTextSchema,
  sortOrderSchema,
  uuidSchema,
} from '../primitives.js';
import {
  EXPENSE_SOURCES, PAYMENT_METHODS } from '../enums.js';

export const createExpenseSchema = z.object({
  amount: positiveMoneySchema,
  categoryId: uuidSchema.nullable().optional(),
  /**
   * Convenience for fast entry and for CSV import: name an existing category
   * or create it on the fly. Ignored when `categoryId` is supplied.
   */
  categoryName: shortTextSchema.optional(),
  description: longTextSchema.optional(),
  /** The staff member the expense is attributed to, not the person recording it. */
  staffId: uuidSchema.nullable().optional(),
  branchId: uuidSchema.nullable().optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).default('cash'),
  incurredAt: isoDateTimeSchema.optional(),
  /** Id returned by POST /attachments: the receipt or invoice. */
  attachmentId: uuidSchema.nullable().optional(),
  notes: longTextSchema.optional(),
  /** Who was paid: the landlord, the electricity company, the repairer. */
  vendor: shortTextSchema.nullable().optional(),
  /**
   * False records a bill that has not been paid yet: a cost from its date,
   * but no money moves until a payment is recorded. Left out, an expense
   * dated today or earlier is paid on its date (and one dated later never is).
   */
  paid: z.boolean().optional(),
  /**
   * The cash or bank account it was actually paid from. Left out, it comes
   * out of the location's default account for the payment method.
   */
  accountId: uuidSchema.nullable().optional(),
  /** One per submission: a retried or double-clicked save records one expense. */
  idempotencyKey: z.string().trim().min(8).max(100).optional(),
});
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

// Whether it was paid is answered by recording payments, never by an edit.
export const updateExpenseSchema = createExpenseSchema.omit({ paid: true }).partial();
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

/**
 * Where an expense stands. Derived, never stored: `pending`, `partial` and
 * `paid` are its payment state, and `cancelled` is a deleted expense.
 */
export const EXPENSE_STATUSES = ['pending', 'partial', 'paid', 'cancelled'] as const;
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];
export const EXPENSE_STATUS_LABELS: Record<ExpenseStatus, string> = {
  pending: 'Not paid yet',
  partial: 'Partly paid',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

export const expenseFiltersSchema = paginationSchema.merge(dateRangeFields).extend({
  search: z.string().trim().max(120).optional(),
  /** Left out: every expense that stands. `cancelled` is the only way to list cancelled ones. */
  status: z.enum(EXPENSE_STATUSES).optional(),
  categoryId: uuidSchema.optional(),
  staffId: uuidSchema.optional(),
  branchId: uuidSchema.optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  sortBy: z.enum(['incurredAt', 'amount', 'createdAt']).default('incurredAt'),
  sortOrder: sortOrderSchema,
}).refine((v: { from?: string | undefined; to?: string | undefined }) => isOrderedRange(v), { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });
export type ExpenseFilters = z.infer<typeof expenseFiltersSchema>;

export const expenseSchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  reference: z.string(),
  amount: z.string(),
  currencyCode: z.string(),
  categoryId: uuidSchema.nullable(),
  categoryName: z.string().nullable(),
  description: z.string().nullable(),
  notes: z.string().nullable(),
  staffId: uuidSchema.nullable(),
  staffName: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  paymentMethod: z.enum(PAYMENT_METHODS),
  incurredAt: z.string(),
  attachmentId: uuidSchema.nullable(),
  attachmentUrl: z.string().nullable(),
  /** Null for an automatically generated occurrence: nobody pressed anything. */
  recordedByUserId: uuidSchema.nullable(),
  recordedByName: z.string().nullable(),
  /** How this row came to exist. See EXPENSE_SOURCES. */
  source: z.enum(EXPENSE_SOURCES),
  /** The recurring schedule this satisfies, for either kind. Null for a one-off. */
  scheduleId: uuidSchema.nullable(),
  /** The scheduled date it satisfies, which is not always when it was paid. */
  occurrenceOn: z.string().nullable(),
  /**
   * Whether the money has actually left (0154). A cost recorded ahead of its
   * date — a recurring expense taken in advance, or created by the schedule —
   * is unpaid until somebody records the payment; Cash & Bank moves money only
   * then, on the day it was paid.
   */
  paymentState: z.enum(['paid', 'partial', 'unpaid']),
  /** When it was fully paid: the last payment. Null until then. */
  paidAt: z.string().nullable(),
  /** The account it was paid from, when one was named. */
  accountId: uuidSchema.nullable(),
  vendor: z.string().nullable(),
  /** What has been paid of it so far: all of it once paid, none while unpaid. */
  amountPaid: z.string(),
  /** What is still to pay. Zero once paid, and on a cancelled expense. */
  outstanding: z.string(),
  status: z.enum(EXPENSE_STATUSES),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  /**
   * Set when the owner marked this cancelled expense as test data (0164): it
   * moves no money in Cash & bank or the expense summary. Kept, never deleted.
   */
  testEntry: z.object({ at: z.string(), reason: z.string(), byName: z.string().nullable() }).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Expense = z.infer<typeof expenseSchema>;

/**
 * A payment towards an unpaid or partly paid expense: when the money actually
 * left, and from where. Without an amount it pays what is still owed.
 */
export const recordExpensePaymentSchema = z.object({
  paidOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a date in YYYY-MM-DD format'),
  amount: positiveMoneySchema.optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  accountId: uuidSchema.nullable().optional(),
  reference: shortTextSchema.optional(),
});
export type RecordExpensePaymentInput = z.infer<typeof recordExpensePaymentSchema>;

export const expensePaymentSchema = z.object({
  id: uuidSchema,
  amount: z.string(),
  paidAt: z.string(),
  paymentMethod: z.enum(PAYMENT_METHODS),
  accountId: uuidSchema.nullable(),
  reference: z.string().nullable(),
  recordedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type ExpensePayment = z.infer<typeof expensePaymentSchema>;

/** One expense with its payments, oldest first. */
export const expenseDetailSchema = expenseSchema.extend({ payments: z.array(expensePaymentSchema) });
export type ExpenseDetail = z.infer<typeof expenseDetailSchema>;

/**
 * Cancelling an expense entered in error, or a bill that will not be paid.
 * Nothing is erased: it leaves every total, and money already paid on it
 * comes back on the day it is cancelled — never by rewriting the day it left.
 */
export const cancelExpenseSchema = z.object({
  reason: shortTextSchema,
});
export type CancelExpenseInput = z.infer<typeof cancelExpenseSchema>;

/** Marking a cancelled expense as test data, or undoing that. The owner's, always with a reason. */
export const markTestEntrySchema = z.object({
  testEntry: z.boolean(),
  reason: shortTextSchema.refine((v) => v.trim().length >= 3, 'Say why'),
});
export type MarkTestEntryInput = z.infer<typeof markTestEntrySchema>;

// ── Summary ─────────────────────────────────────────────────────────────────

export const EXPENSE_SUMMARY_BUCKETS = ['day', 'week', 'month'] as const;
export type ExpenseSummaryBucket = (typeof EXPENSE_SUMMARY_BUCKETS)[number];

export const expenseSummaryQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    bucket: z.enum(EXPENSE_SUMMARY_BUCKETS).default('day'),
    branchId: uuidSchema.optional(),
    /** Left out: the business's own currency. */
    currencyCode: z.string().trim().length(3).toUpperCase().optional(),
  })
  .refine((v) => v.from <= v.to, { message: ORDERED_RANGE_ISSUE.message, path: ['from'] })
  // Ten years at most: one bucket per day across centuries is a query, not a report.
  .refine((v) => Date.parse(v.to) - Date.parse(v.from) <= 3660 * 86_400_000, {
    message: 'Choose a period of ten years or less',
    path: ['from'],
  });
export type ExpenseSummaryQuery = z.infer<typeof expenseSummaryQuerySchema>;

export interface ExpenseSummaryLine {
  key: string | null;
  label: string;
  amount: string;
  count: number;
}

/**
 * Spending between two dates.
 *
 *   incurred     costs dated in the period, paid or not — what profit subtracts
 *   paid         money that actually left in the period, on any expense —
 *                Cash & bank's Expenses paid, cancelled expenses included
 *   returned     money paid on expenses cancelled in the period, coming back
 *   outstanding  still to pay, on costs dated up to the period's end
 */
export interface ExpenseSummary {
  from: string;
  to: string;
  currencyCode: string;
  /** Other currencies with expenses in the period, so the screen can offer a switch. */
  currencies: string[];
  incurred: string;
  count: number;
  paid: string;
  returned: string;
  outstanding: string;
  outstandingCount: number;
  byCategory: ExpenseSummaryLine[];
  byBranch: ExpenseSummaryLine[];
  byVendor: ExpenseSummaryLine[];
  byStatus: Record<'pending' | 'partial' | 'paid', { amount: string; count: number }>;
  /** Every bucket in the period, an empty one as zero. */
  series: Array<{ bucket: string; incurred: string; paid: string }>;
}

// ── Categories ──────────────────────────────────────────────────────────────

export const createExpenseCategorySchema = z.object({
  name: shortTextSchema,
  description: longTextSchema.optional(),
});
export type CreateExpenseCategoryInput = z.infer<typeof createExpenseCategorySchema>;

export const expenseCategorySchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  createdAt: z.string(),
});
export type ExpenseCategory = z.infer<typeof expenseCategorySchema>;

/**
 * Seeded for every new business so the expense form is usable immediately.
 * Deliberately industry-neutral — industry modules add their own in Phase 2
 * (e.g. "Raw Water"/"Bottles" for water production, "Fuel" for logistics).
 */
export const DEFAULT_EXPENSE_CATEGORIES = [
  'Rent',
  'Utilities',
  'Salaries & Wages',
  'Supplies',
  'Inventory / Stock',
  'Transport & Fuel',
  'Maintenance & Repairs',
  'Marketing',
  'Bank Charges',
  'Taxes & Levies',
  'Miscellaneous',
] as const;
